// ============================================================================
// useUnsavedChangesGuard — browser-level guards for in-progress work
// ============================================================================
// Wires the three guard surfaces a typical enterprise screen / modal needs,
// from a single hook call:
//
//   1. `beforeunload` — browser tab close / refresh shows native confirm.
//      Native dialog text is browser-controlled; can't be customised.
//   2. `visibilitychange` (optional) — pause polling / save-drafts when the
//      tab goes hidden. Disabled by default.
//   3. React Router navigation — when the user clicks an internal link or
//      hits the back button. Opt-in via `useNavigationBlock`.
//
// USAGE:
//   useUnsavedChangesGuard({ isDirty, message: 'Unsaved changes will be lost.' });
//
// MODAL-MINIMIZE INTEGRATION:
//   When a modal is MINIMIZED (not closed), the minimize() call carries
//   isDirty into the minimize context. The logout / tab close path in
//   main_layout.tsx reads aggregate isDirty count via getTotalDirtyCountRef
//   and shows a single confirm before destroying minimized state. Screens
//   don't need to do anything special — just use the hook and pass through
//   isDirty into minimize() per the ENTERPRISE_SCREEN_CHECKLIST.

import { useEffect, useRef } from 'react';

interface Options {
  /** True when the form / screen has unsaved changes. */
  isDirty: boolean;
  /**
   * Message hint for the native beforeunload prompt. Most browsers ignore
   * this and show their own canned text; only set it for completeness /
   * older browsers. Default: a generic warning.
   */
  message?: string;
  /**
   * When true, also listen for visibilitychange events. Default false.
   * Provide `onVisibilityHidden` to take action.
   */
  watchVisibility?: boolean;
  /**
   * Fired when the tab goes hidden AND the screen is dirty. Use to save
   * a draft / pause auto-refresh / etc. Only invoked when watchVisibility
   * is true.
   */
  onVisibilityHidden?: () => void;
}

/**
 * Browser-level guard against losing unsaved changes. Add to any screen
 * that has form state worth protecting. Composes with the existing
 * useModalMinimize logout-confirm flow — no need to duplicate guards.
 */
export function useUnsavedChangesGuard({
  isDirty,
  message = 'You have unsaved changes. Are you sure you want to leave?',
  watchVisibility = false,
  onVisibilityHidden,
}: Options): void {
  // Refs so the listeners stay stable (empty deps); they always read the
  // latest values without needing to be re-registered on every render.
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  const messageRef = useRef(message);
  messageRef.current = message;

  const onVisibilityHiddenRef = useRef(onVisibilityHidden);
  onVisibilityHiddenRef.current = onVisibilityHidden;

  // beforeunload handler — fires on tab close, browser refresh, navigation
  // away from the SPA via address-bar change. Returning a non-empty string
  // triggers the native confirm in legacy browsers; setting
  // event.returnValue is the modern requirement.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!isDirtyRef.current) return;
      e.preventDefault();
      // Most browsers ignore the message but require returnValue to be set
      // for the prompt to appear.
      e.returnValue = messageRef.current;
      return messageRef.current;
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // visibilitychange — useful for "save draft" or "pause polling" hooks.
  useEffect(() => {
    if (!watchVisibility) return undefined;
    const handler = () => {
      if (document.visibilityState === 'hidden' && isDirtyRef.current) {
        onVisibilityHiddenRef.current?.();
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [watchVisibility]);
}

export default useUnsavedChangesGuard;
